/**
 * ContactCTA.tsx · W128.6 · 2026-05-26.
 *
 * Sticky bottom CTA bar (mobile) + inline section (all viewports) que
 * dá ao prospect dois caminhos rápidos:
 *
 *   1. WhatsApp deeplink — abre conversa direto com o construtor com
 *      mensagem pré-preenchida ("Olá! Vi a vitrine de <nome>. Pode me
 *      passar mais detalhes?").
 *   2. Solicitar proposta — scroll suave pro <LeadForm /> (#lead).
 *
 * Por que sticky bottom só no mobile: a vitrine inteira é cinematic
 * scrolly e o footer fixo no desktop competiria com a Hero / Approach.
 * No mobile, o buyer scrolla com o polegar e precisa de um atalho
 * permanente — a barra de 56pt fica acima do safe-area e nunca cobre
 * conteúdo crítico (todos os blocos têm padding-bottom suficiente).
 *
 * Quando builderWhatsappFromEnv() retorna null (build sem
 * VITE_BUILDER_WHATSAPP), o botão WhatsApp esconde e mantém só o
 * Lead — better silent than broken.
 */

import { useEffect, useState } from 'react';
import type { PublicShowcase } from '../lib/api';
import { builderWhatsappFromEnv, whatsappDeeplink } from '../lib/whatsapp';
import { useLocale } from '../lib/i18n';

interface Props {
  data: PublicShowcase;
}

function projectDisplayName(data: PublicShowcase): string {
  // Prefer hero headline → eyebrow → slug. Same precedence
  // PublicHousy.tsx uses for `projectName` em wave40.ts.
  return (
    data.heroHeadline?.trim() ||
    data.heroEyebrow?.trim() ||
    data.slug
  );
}

function buildPrefilledMessage(data: PublicShowcase, locale: 'pt-BR' | 'en'): string {
  const name = projectDisplayName(data);
  if (locale === 'en') {
    return `Hi! I just saw the ${name} showcase. Could you share more details?`;
  }
  return `Olá! Vi a vitrine de ${name}. Pode me passar mais detalhes?`;
}

function scrollToLead(e?: React.MouseEvent) {
  e?.preventDefault();
  const el = document.getElementById('lead');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else if (typeof window !== 'undefined') {
    // Lead form ainda não montou (cenários muito longos). Cai no
    // hash — o LeadForm escuta o anchor.
    window.location.hash = '#lead';
  }
}

/**
 * Inline contact band. Sits between Investimento e MemorialFlipbook,
 * antes da feed final de lead. Tipo brochure-stop: "ainda em dúvida?
 * Conversa direta".
 */
export function ContactCTA({ data }: Props) {
  const [locale] = useLocale();
  const builderPhone = builderWhatsappFromEnv();
  const message = buildPrefilledMessage(data, locale);
  const waHref = whatsappDeeplink(builderPhone, message);
  const hasWhatsapp = builderPhone !== null;

  // Wave 78 carries pt-BR copy by default; EN flips minor labels.
  const labels = locale === 'en'
    ? {
        eyebrow: 'Talk to us',
        title: 'Two ways to start the conversation.',
        body:
          'Open WhatsApp for an instant chat with the builder, or send a structured request and we reply within one business day.',
        whatsapp: 'Chat on WhatsApp',
        lead: 'Send a proposal request',
      }
    : {
        eyebrow: 'Fale com o construtor',
        title: 'Duas formas de começar a conversa.',
        body:
          'Abra o WhatsApp pra falar agora com o construtor, ou envie uma proposta estruturada — respondemos em até um dia útil.',
        whatsapp: 'Falar no WhatsApp',
        lead: 'Enviar proposta detalhada',
      };

  return (
    <section
      className="relative border-t border-line bg-bg py-20 sm:py-28"
      aria-label="Contato"
      data-testid="contact-cta-section"
    >
      <div className="mx-auto max-w-4xl px-6 sm:px-12 lg:px-20">
        <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
          {labels.eyebrow}
        </p>
        <h2 className="mt-5 max-w-[18ch] font-display text-4xl font-light leading-[1.05] tracking-tighter text-ink sm:text-5xl">
          {labels.title}
        </h2>
        <p className="mt-6 max-w-prose text-sm leading-relaxed text-ink-muted sm:text-base">
          {labels.body}
        </p>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:gap-4">
          {hasWhatsapp && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              className="pressable inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 text-[13px] font-medium uppercase tracking-[0.15em] text-white transition-transform duration-150 ease-[cubic-bezier(0.32,0.72,0,1)] hover:scale-[1.02] active:scale-[0.97] focus:ring-2 focus:ring-[#25D366]/40 focus:ring-offset-2 focus:ring-offset-bg"
            >
              <span aria-hidden className="text-lg leading-none">{'\u{1F4AC}'}</span>
              {labels.whatsapp}
            </a>
          )}
          <a
            href="#lead"
            onClick={scrollToLead}
            data-testid="cta-lead"
            className="pressable inline-flex h-12 items-center justify-center rounded-full border border-line bg-transparent px-6 text-[13px] font-medium uppercase tracking-[0.15em] text-ink-muted transition-colors hover:border-ink hover:text-ink active:scale-[0.97]"
          >
            {labels.lead}
          </a>
        </div>
      </div>
    </section>
  );
}

/**
 * Mobile-only sticky bottom CTA bar. Aparece depois do primeiro fold
 * (>30vh scrolled) e some quando o LeadForm já está visível — não há
 * razão pra cobrir o form quando o buyer já chegou nele.
 *
 * Desktop (md+): hidden — desktop tem a inline ContactCTA + Investimento
 * com seu próprio CTA, sem necessidade de barra fixa.
 */
export function StickyMobileCTA({ data }: Props) {
  const [locale] = useLocale();
  const [visible, setVisible] = useState(false);
  const builderPhone = builderWhatsappFromEnv();
  const message = buildPrefilledMessage(data, locale);
  const waHref = whatsappDeeplink(builderPhone, message);
  const hasWhatsapp = builderPhone !== null;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let ticking = false;
    let leadInView = false;
    let scrolled = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        scrolled = window.scrollY > window.innerHeight * 0.3;
        setVisible(scrolled && !leadInView);
        ticking = false;
      });
    };

    // Observer para esconder quando LeadForm está em vista.
    const leadEl = document.getElementById('lead');
    let observer: IntersectionObserver | null = null;
    if (leadEl && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry) {
            leadInView = entry.isIntersecting;
            setVisible(scrolled && !leadInView);
          }
        },
        { threshold: 0.15 },
      );
      observer.observe(leadEl);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, []);

  const labels = locale === 'en'
    ? { whatsapp: 'WhatsApp', lead: 'Get in touch' }
    : { whatsapp: 'WhatsApp', lead: 'Tenho interesse' };

  return (
    <div
      aria-hidden={!visible}
      data-testid="sticky-mobile-cta"
      className={`fixed inset-x-0 bottom-0 z-30 border-t border-line bg-bg/95 backdrop-blur-md transition-transform duration-300 ease-out md:hidden ${
        visible ? 'translate-y-0' : 'translate-y-full pointer-events-none'
      }`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-center gap-2 px-4 py-3">
        {hasWhatsapp && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-cta-whatsapp"
            className="flex h-12 flex-1 items-center justify-center gap-1.5 rounded-full bg-[#25D366] text-[12px] font-medium uppercase tracking-[0.15em] text-white active:scale-[0.97] transition-transform"
          >
            <span aria-hidden className="text-base leading-none">{'\u{1F4AC}'}</span>
            {labels.whatsapp}
          </a>
        )}
        <a
          href="#lead"
          onClick={scrollToLead}
          data-testid="sticky-cta-lead"
          className={`flex h-12 items-center justify-center rounded-full px-5 text-[12px] font-medium uppercase tracking-[0.15em] active:scale-[0.97] transition-transform ${
            hasWhatsapp
              ? 'flex-1 border border-line bg-bg text-ink'
              : 'w-full bg-accent text-bg'
          }`}
        >
          {labels.lead}
        </a>
      </div>
    </div>
  );
}
